import Datadog from '../../utils/trackers/Datadog';

let hasTrackedProfilesConfig = false;

function normalizeBoolean(value) {
  if (value === true || value === false) {
    return value;
  }

  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return null;
}

function getProfileIdentity(profile = {}) {
  const isAdmin = normalizeBoolean(
    Object.prototype.hasOwnProperty.call(profile, 'is_admin') ? profile.is_admin : profile.admin
  );
  const isKids = normalizeBoolean(profile.is_kids);

  return {
    ...(isAdmin === null ? {} : { is_admin: isAdmin }),
    ...(isKids === null ? {} : { is_kids: isKids }),
  };
}

function getPrimaryError(error = {}) {
  if (error.errors && Array.isArray(error.errors) && error.errors[0]) {
    return error.errors[0];
  }

  if (error.response && error.response.errors && Array.isArray(error.response.errors) && error.response.errors[0]) {
    return error.response.errors[0];
  }

  if (error.completeError && error.completeError.errors && Array.isArray(error.completeError.errors) && error.completeError.errors[0]) {
    return error.completeError.errors[0];
  }

  return error;
}

function isSafeFailureReason(value) {
  return typeof value === 'string'
    && value.length > 0
    && value.length <= 64
    && /^[a-z0-9_:-]+$/i.test(value)
    && value.indexOf('http') === -1;
}

function getFailureReason(error = {}) {
  const normalizedError = getPrimaryError(error) || {};
  const candidates = [normalizedError.code, normalizedError.message, error.code, error.message];

  for (let index = 0; index < candidates.length; index += 1) {
    if (isSafeFailureReason(candidates[index])) {
      return candidates[index];
    }
  }

  return 'unknown_error';
}

export function trackProfilesConfigEvaluated(enabled) {
  if (hasTrackedProfilesConfig) {
    return false;
  }

  Datadog.addAction('profiles.config_evaluated', {
    enabled,
  });

  hasTrackedProfilesConfig = true;

  return true;
}

export function trackProfilesLoaded({ members = [], source = 'api' } = {}) {
  const totalProfiles = Array.isArray(members) ? members.length : 0;

  Datadog.addAction('profiles.loaded', {
    result: 'success',
    source,
    members_count: totalProfiles,
    total_profiles: totalProfiles,
  });
}

export function trackProfilesLoadedFailed({ error = {}, source = 'api' } = {}) {
  Datadog.addAction('profiles.loaded_failed', {
    result: 'error',
    source,
    failure_reason: getFailureReason(error),
  });
}

export function trackProfileSelected(profile = {}) {
  Datadog.addAction('profiles.selected', {
    ...getProfileIdentity(profile),
  });
}

export function trackProfilesJourneyComplete({ profile = {}, durationMs = 0 } = {}) {
  Datadog.addAction('profiles.journey.complete', {
    ...getProfileIdentity(profile),
    duration_ms: durationMs,
    total_time_ms: durationMs,
  });
}

export function trackProfilesAvatarsLoaded({ avatars = [] } = {}) {
  Datadog.addAction('profiles.avatars.loaded', {
    result: 'success',
    avatars_count: Array.isArray(avatars) ? avatars.length : 0,
  });
}

export function trackProfilesAvatarsLoadFailed({ error = {} } = {}) {
  Datadog.addAction('profiles.avatars.load_failed', {
    result: 'error',
    failure_reason: getFailureReason(error),
  });
}

export function trackProfilesCreateSubmitted(profile = {}) {
  Datadog.addAction('profiles.create.submitted', getProfileIdentity(profile));
}

export function trackProfilesCreateSuccess(profile = {}) {
  Datadog.addAction('profiles.create.success', getProfileIdentity(profile));
}

export function trackProfilesCreateFailed({ profile = {}, error = {} } = {}) {
  Datadog.addAction('profiles.create.failed', {
    ...getProfileIdentity(profile),
    failure_reason: getFailureReason(error),
  });
}

export function trackProfilesEditSubmitted(profile = {}) {
  Datadog.addAction('profiles.edit.submitted', getProfileIdentity(profile));
}

export function trackProfilesEditSuccess(profile = {}) {
  Datadog.addAction('profiles.edit.success', getProfileIdentity(profile));
}

export function trackProfilesEditFailed({ profile = {}, error = {} } = {}) {
  Datadog.addAction('profiles.edit.failed', {
    ...getProfileIdentity(profile),
    failure_reason: getFailureReason(error),
  });
}

export function trackProfilesDeleteInitiated(profile = {}) {
  Datadog.addAction('profiles.delete.initiated', getProfileIdentity(profile));
}

export function trackProfilesDeleteConfirmed(profile = {}) {
  Datadog.addAction('profiles.delete.confirmed', getProfileIdentity(profile));
}

export function trackProfilesDeleteCancelled(profile = {}) {
  Datadog.addAction('profiles.delete.cancelled', getProfileIdentity(profile));
}

export function trackProfilesAvatarSelectorOpened(context = {}) {
  Datadog.addAction('profiles.avatar.selector.opened', {
    origin: context.origin || '',
    ...getProfileIdentity(context.profile),
  });
}

export function trackProfilesAvatarSelectorSelected(context = {}) {
  Datadog.addAction('profiles.avatar.selector.selected', {
    origin: context.origin || '',
    ...getProfileIdentity(context.profile),
  });
}

export function trackProfilesAvatarSelectorDismissed(context = {}) {
  Datadog.addAction('profiles.avatar.selector.dismissed', {
    origin: context.origin || '',
    ...getProfileIdentity(context.profile),
  });
}

export function trackProfilesSwitchInitiated(profile = {}) {
  Datadog.addAction('profiles.switch.initiated', getProfileIdentity(profile));
}

export function trackProfilesSwitchSuccess(profile = {}) {
  Datadog.addAction('profiles.switch.success', getProfileIdentity(profile));
}

export function trackProfilesSwitchFailed({ profile = {}, error = {} } = {}) {
  Datadog.addAction('profiles.switch.failed', {
    ...getProfileIdentity(profile),
    failure_reason: getFailureReason(error),
  });
}


// WEBPACK FOOTER //
// ./src/services/datadog/profile.js